
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface EntrepriseSettings {
  id?: number;
  nom: string;
  adresse: string;
  telephone: string;
  email: string;
  matricule_fiscal: string;
  rib: string;
  logo_url?: string;
  site_web?: string;
}

// Cache global pour éviter les requêtes répétées
let settingsCache: EntrepriseSettings | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let fetchPromise: Promise<EntrepriseSettings | null> | null = null;

/**
 * Fetches the entreprise settings once, with caching and request deduplication.
 */
const fetchSettingsOnce = async (): Promise<EntrepriseSettings | null> => {
  // Return cached value if still fresh
  if (settingsCache && Date.now() - cacheTimestamp < CACHE_DURATION) {
    return settingsCache;
  }

  // If a fetch is already in progress, return the same promise
  if (fetchPromise) return fetchPromise;

  // Create a new fetch promise
  fetchPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('entreprise_settings')
        .select('*')
        .maybeSingle();

      if (error) throw error;

      // Update cache
      settingsCache = data;
      cacheTimestamp = Date.now();

      return data;
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      // Return the stale cache (if any) instead of throwing
      return settingsCache;
    } finally {
      // Reset the promise so subsequent calls can trigger a new request
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

export const useParametres = () => {
  const [settings, setSettings] = useState<EntrepriseSettings | null>(settingsCache);
  const [loading, setLoading] = useState(!settingsCache);

  /**
   * Retrieves settings, optionally forcing a cache refresh.
   */
  const fetchSettings = async (forceRefresh = false) => {
    if (forceRefresh) {
      cacheTimestamp = 0; // Invalidate cache
    }

    try {
      const data = await fetchSettingsOnce();
      setSettings(data);
    } finally {
      setLoading(false);
    }
  };

  // Initialise on mount
  useEffect(() => {
    if (settingsCache && Date.now() - cacheTimestamp < CACHE_DURATION) {
      setSettings(settingsCache);
      setLoading(false);
    } else {
      fetchSettings();
    }
  }, []);

  /**
   * Updates (or creates) the entreprise settings in Supabase.
   */
  const updateSettings = async (newSettings: Partial<EntrepriseSettings>) => {
    try {
      // Check if a row already exists
      const { data: existing, error: fetchError } = await supabase
        .from('entreprise_settings')
        .select('id')
        .maybeSingle();

      if (fetchError) throw fetchError;

      let result;

      if (existing && existing.id) {
        // Update existing row
        result = await supabase
          .from('entreprise_settings')
          .update(newSettings)
          .eq('id', existing.id)
          .select()
          .single();
      } else {
        // Insert a new row; provide a default name if none is supplied
        result = await supabase
          .from('entreprise_settings')
          .insert([{ nom: 'XtremeDigital', ...newSettings }])
          .select()
          .single();
      }

      if (result.error) throw result.error;

      // Refresh cache and state
      settingsCache = result.data;
      cacheTimestamp = Date.now();
      setSettings(result.data);

      return { success: true };
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour des paramètres:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Uploads a logo file via a Supabase Edge Function.
   */
  const uploadLogo = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/upload-logo`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || "Erreur lors de l'upload" };
      }

      // Invalidate the cache so the next fetch gets the new logo URL
      cacheTimestamp = 0;

      return { success: true, logo_url: result.logo_url };
    } catch (error: any) {
      console.error('Erreur uploadLogo:', error);
      return { success: false, error: error.message || "Erreur lors de l'upload du logo" };
    }
  };

  return {
    settings,
    loading,
    updateSettings,
    uploadLogo,
    refetch: () => fetchSettings(true),
  };
};
